import { iconJpNameList, IconName } from "components/common/Icon";
import ExcelJS from "exceljs";
import { useSubject } from "hooks/api/useSubject";
import { decideRecord } from "pages/MonitorManagement/service";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import { SheetDownloadType } from "types/excelType";
import {
  ChatData,
  ChatDataType,
  Monitor,
  MonitorAttribute,
  ResourceType,
} from "types/graphqlapi";
import { listChatDataByInterviewId } from "utils/apis/chatData";
import { getFileResoucesByFileUrl } from "utils/apis/fileResource";
import {
  getInterviewByIdQuery,
  listInterviewsQuery,
} from "utils/apis/interview";
import { fetchInterviewerAnnounceByInterviewIdQuery } from "utils/apis/interviewerAnnounce";
import { fetchMonitorLatestAssignByMonitorIdQuery } from "utils/apis/monitorAssign";
import { fetchMonitorAttributeByMonitorIdQuery } from "utils/apis/monitorAttribute";
import { fetchScenarioBysubjectIdQuery } from "utils/apis/scenario";
import { formatDateForDisplay, getSlashDate } from "utils/common";

export const useExcelJS = () => {
  const { selectedSubjectId } = useConsoleState();
  const { clientSubjectData, getMonitorListBySubjectId } =
    useSubject(selectedSubjectId);
  const { setLoading } = useLoadingState();

  const createSubjectSheet = async () => {
    const subjectSheet: SheetDownloadType = {
      sheetName: "案件概要",
      columns: SubjectDownloadColumns,
      dataList: [clientSubjectData],
    };

    return subjectSheet;
  };

  const createScenarioSheet = async () => {
    const scenarioList = await fetchScenarioBysubjectIdQuery(
      selectedSubjectId
    ).then((list) =>
      list.map((scenario) => ({
        text: scenario.template_no === 0 ? scenario.text : `,${scenario.text}`,
      }))
    );

    const scenarioSheet: SheetDownloadType = {
      sheetName: "インタビューフロー",
      columns: ScenarioDownloadColumns,
      dataList: scenarioList,
    };

    return scenarioSheet;
  };

  const createMonitorSheet = async (monitorList: Monitor[]) => {
    const monitorDownloadData = await Promise.all(
      monitorList.map(async (monitor) => {
        const interviewId = await fetchMonitorLatestAssignByMonitorIdQuery(
          monitor.id
        ).then((value) => value?.interview_id);

        const attributeList = await fetchMonitorAttributeByMonitorIdQuery(
          monitor.id
        );

        attributeList.map((attribute) => {
          Object.assign(monitor, {
            [attribute.seq_no.toString()]: attribute.value,
          });
        });

        if (interviewId) {
          const interview = await getInterviewByIdQuery(interviewId);

          const monitorInfo = {
            ...monitor,
            interview_seq: interview?.interview_seq,
            interview_start_date: formatDateForDisplay(
              interview?.scheduled_start_date
            ),
            start_time: interview?.scheduled_start_time,
            interview_end_date: formatDateForDisplay(
              interview?.scheduled_end_date
            ),
            end_time: interview?.scheduled_end_time,
            record: await decideRecord(monitor.id),
          };

          return monitorInfo;
        }
        return monitor;
      })
    );

    const subjectMonitorAttributeList =
      await fetchMonitorAttributeByMonitorIdQuery(monitorList[0].id);

    const monitorSheet: SheetDownloadType = {
      sheetName: "モニター情報",
      columns: getMonitorDownloadColumns(subjectMonitorAttributeList),
      dataList: monitorDownloadData,
    };

    return monitorSheet;
  };

  const createChatSheetList = async (monitorList: Monitor[]) => {
    const monitorRoster: { [id: string]: string } = {};
    monitorList.forEach((monitor) => {
      Object.assign(monitorRoster, { [monitor.id]: monitor.name });
    });

    const interviewList = await listInterviewsQuery(selectedSubjectId);

    if (interviewList) {
      const chatSheetList = await Promise.all(
        interviewList.map(async (interview) => {
          const chatDataList = await listChatDataByInterviewId(
            interview.id
          ).then((list) =>
            list.filter(
              (chat) =>
                chat.type !== ChatDataType.SYSTEMLOG &&
                chat.type !== ChatDataType.TYPING
            )
          );

          const interviewerAnnounceList =
            await fetchInterviewerAnnounceByInterviewIdQuery(interview.id);

          const withAnnounceList = [
            ...chatDataList,
            ...(interviewerAnnounceList as unknown as ChatData[]),
          ];
          withAnnounceList.sort((a, b) =>
            a.createdAt && b.createdAt && a.createdAt > b.createdAt ? 1 : -1
          );

          const chatDownloadData: {
            sent_date: string;
            sender_name: string;
            content?: string;
          }[] = [];

          const mentionedMonitorIdListToString = (
            mentioned_monitor_id_list?: string | null
          ) =>
            mentioned_monitor_id_list
              ? `${mentioned_monitor_id_list
                  .split(",")
                  .map((id) => monitorList.find((monitor) => monitor.id === id))
                  .map((monitor) => `@${monitor?.name || ""}`)
                  .join(" ")} `
              : "";

          for (const chat of withAnnounceList) {
            const sentDate = getSlashDate(new Date(chat.createdAt));
            const senderName = !chat.type //InterviewerAnnounceの時
              ? "インタビュアー(アナウンス)"
              : chat.monitor_id
              ? monitorRoster[chat.monitor_id]
              : "インタビュアー";
            const chatInfo = {
              sent_date: sentDate,
              sender_name: senderName,
            };

            if (!chat.type) {
              Object.assign(chatInfo, { content: chat.text || "(削除)" }); //InterviewerAnnounceの時
            } else if (chat.type === ChatDataType.TEXT) {
              Object.assign(chatInfo, {
                content: `${mentionedMonitorIdListToString(
                  chat.mentioned_monitor_id_list
                )}${chat.text}`,
              });
            } else if (chat.type === ChatDataType.ICON) {
              const iconName = chat.text as IconName;

              Object.assign(chatInfo, {
                content: `${mentionedMonitorIdListToString(
                  chat.mentioned_monitor_id_list
                )}アイコン：${iconJpNameList[iconName]}`,
              });
            } else if (chat.type === ChatDataType.FILE) {
              const fileTypeText =
                chat.file_type === ResourceType.IMAGE ? "画像" : "動画";

              if (chat.moderator_id) {
                const getContent = async () => {
                  if (chat.text) {
                    return chat.text;
                  } else if (chat.file_url) {
                    const fileTitle = await getFileResoucesByFileUrl(
                      selectedSubjectId,
                      chat.file_url
                    ).then((file) => file?.title);

                    return `${fileTypeText}${
                      fileTitle ? `「${fileTitle}」` : ""
                    }をアップロードしました`;
                  } else {
                    return `${fileTypeText}をアップロードしました`;
                  }
                };

                const content = await getContent();

                Object.assign(chatInfo, {
                  content: `${mentionedMonitorIdListToString(
                    chat.mentioned_monitor_id_list
                  )}${content}`,
                });
              } else if (chat.monitor_id) {
                const content = chat.text
                  ? chat.text
                  : `${fileTypeText}をアップロードしました`;
                Object.assign(chatInfo, {
                  content: `${mentionedMonitorIdListToString(
                    chat.mentioned_monitor_id_list
                  )}${content}`,
                });
              }
            }

            chatDownloadData.push(chatInfo);
          }

          const chatSheet: SheetDownloadType & { seq: number } = {
            sheetName: `チャット_${interview.interview_seq}`,
            columns: ChatDownloadColumns,
            dataList: chatDownloadData,
            seq: interview.interview_seq,
          };
          return chatSheet;
        })
      ).then((list) => list.slice().sort((a, b) => (a.seq < b.seq ? -1 : 1)));

      return chatSheetList;
    }
  };

  const createSheetList = async (): Promise<
    [
      Promise<SheetDownloadType | undefined>,
      Promise<SheetDownloadType | undefined>,
      Promise<SheetDownloadType> | undefined,
      Promise<(SheetDownloadType & { seq: number })[] | undefined> | undefined
    ]
  > => {
    const subjectSheet = createSubjectSheet();
    const scenarioSheet = createScenarioSheet();

    const monitorList = await getMonitorListBySubjectId();
    if (monitorList.length === 0)
      return [subjectSheet, scenarioSheet, undefined, undefined];

    const monitorSheet = createMonitorSheet(monitorList);
    const chatSheetList = createChatSheetList(monitorList);

    return [subjectSheet, scenarioSheet, monitorSheet, chatSheetList];
  };

  const downloadExcel = async () => {
    if (!clientSubjectData?.subject_name) return;

    setLoading(true);

    const [subjectSheet, scenarioSheet, monitorSheet, chatSheetList] =
      await Promise.all(await createSheetList());
    if (!subjectSheet || !scenarioSheet) return;

    const downloadSheetList = [subjectSheet, scenarioSheet];

    if (monitorSheet) {
      downloadSheetList.push(monitorSheet);

      if (chatSheetList && chatSheetList.length >= 0) {
        downloadSheetList.push(...chatSheetList);
      }
    }

    await excelDownload(clientSubjectData?.subject_name, downloadSheetList);

    setLoading(false);
  };

  return { downloadExcel };
};

const excelDownload = async (bookName: string, sheets: SheetDownloadType[]) => {
  const workbook = new ExcelJS.Workbook();

  sheets.map(async (sheet) => {
    workbook.addWorksheet(sheet.sheetName);
    const worksheet = workbook.getWorksheet(sheet.sheetName);
    worksheet.columns = sheet.columns;
    worksheet.addRows(sheet.dataList);
    sheet.colWidths &&
      (await sheet.colWidths.forEach((value, index) => {
        worksheet.getColumn(index + 1).width = value;
      }));
  });

  const uint8Array = await workbook.xlsx.writeBuffer();
  const blob = new Blob([uint8Array], { type: "application/octet-binary" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${bookName}.xlsx`;
  a.target = "_blank";
  a.click();
  a.remove();
};

const SubjectDownloadColumns = [
  { header: "案件ID", key: "subject_number" },
  { header: "案件名", key: "subject_name" },
  { header: "ルームタイプ", key: "interviewTypeDescription" },
  { header: "案件概要", key: "subject_description" },
  { header: "案件目的", key: "subject_purpose" },
  { header: "アスマーク主担当", key: "main_user_name_asmarq" },
  { header: "グループ主担当", key: "main_user_name_group" },
  { header: "ステータス", key: "status" }, //db非管理
  { header: "備考", key: "memo" },
];

const getMonitorDownloadColumns = (attributeList: MonitorAttribute[]) => [
  { header: "管理ID", key: "ans_id" },
  { header: "グループ / 割付", key: "classification" },
  { header: "開始日", key: "interview_start_date" },
  { header: "開始時刻", key: "start_time" },
  { header: "終了日", key: "interview_end_date" },
  { header: "終了時刻", key: "end_time" },
  { header: "実査No.", key: "interview_seq" },
  { header: "実績", key: "record" },
  { header: "モニターID", key: "member_id" },
  { header: "性別", key: "sex" },
  { header: "年齢", key: "age" },
  { header: "居住地", key: "address" },
  { header: "既婚有無", key: "marriage" },
  { header: "子有無", key: "children" },
  { header: "職業", key: "job" },
  ...attributeList?.map((attribute) => {
    return { header: attribute.name, key: attribute.seq_no.toString() };
  }),
];

const ChatDownloadColumns = [
  { header: "投稿日時", key: "sent_date" },
  { header: "投稿者名", key: "sender_name" },
  { header: "内容", key: "content" },
];

const ScenarioDownloadColumns = [{ header: "インタビューフロー", key: "text" }];
